import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

// Tally Sync Center — Mar 3, 2026 · one-click Sales + Purchase XML export
// that feeds directly into Tally's `Import Data` flow. Docs are stamped
// `tally_sync_status: synced` after export so re-runs never duplicate.

export default function TallySync() {
  const [counts, setCounts] = useState({
    sales: { pending: 0, synced: 0 },
    purchase: { pending: 0, synced: 0 },
  });
  const [busy, setBusy] = useState({ sales: false, purchase: false });
  const [runs, setRuns] = useState([]);
  const [reconcileRow, setReconcileRow] = useState(null); // {id, kind, count}
  const [voucherText, setVoucherText] = useState("");

  const load = async () => {
    try {
      const [{ data: c }, { data: r }] = await Promise.all([
        api.get("/tally/pending"),
        api.get("/tally/runs?limit=20"),
      ]);
      setCounts(c);
      setRuns(r || []);
    } catch (e) {
      toast.error("Failed to load Tally sync status");
    }
  };
  useEffect(() => { load(); }, []);

  const downloadXml = async (kind) => {
    setBusy((s) => ({ ...s, [kind]: true }));
    try {
      const path = kind === "sales" ? "/tally/sales-xml" : "/tally/purchase-xml";
      const res = await api.post(path, {}, { responseType: "blob" });
      const count = res.headers["x-exported-count"] || "?";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const label = kind === "sales" ? "tally-sales" : "tally-purchase";
      a.download = `${label}-${new Date().toISOString().slice(0,19).replace(/[:.]/g, "")}.xml`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${count} ${kind === "sales" ? "invoice" : "bill"}${count === "1" ? "" : "s"} exported → marked Synced`);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Export failed";
      toast.error(detail);
    } finally {
      setBusy((s) => ({ ...s, [kind]: false }));
    }
  };

  const KpiCard = ({ label, value, tone = "default" }) => {
    const toneClass = tone === "warn" ? "text-amber-400" : tone === "ok" ? "text-emerald-400" : "text-white";
    return (
      <div className="flex flex-col gap-1 rounded-lg border border-[var(--border)] bg-[var(--bg-2)] px-4 py-3">
        <span className="text-[10px] uppercase tracking-wider text-[var(--muted)]">{label}</span>
        <span className={`text-2xl font-semibold tabular-nums ${toneClass}`}>{value.toLocaleString("en-IN")}</span>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6" data-testid="tally-sync-center">
      <div>
        <div className="uppercase tracking-widest text-[10px] text-[var(--muted)]">Finance</div>
        <h1 className="text-3xl font-display text-white">Tally Sync Center</h1>
        <p className="text-sm text-[var(--muted)]">
          Export authorized GST invoices &amp; GRNs as Tally-compatible XML. Each doc
          is stamped <span className="text-emerald-400">Synced</span> after export so
          your next run only includes fresh entries.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* --- Sales card ------------------------------------------------ */}
        <Card className="bg-[var(--bg-1)] border-[var(--border)]" data-testid="tally-sales-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              Sales · GST Invoices
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <KpiCard label="Pending Sync" value={counts.sales.pending} tone="warn" />
              <KpiCard label="Synced" value={counts.sales.synced} tone="ok" />
            </div>
            <Button
              onClick={() => downloadXml("sales")}
              disabled={busy.sales || counts.sales.pending === 0}
              className="w-full"
              data-testid="btn-tally-sales-xml"
            >
              {busy.sales
                ? "Generating…"
                : counts.sales.pending === 0
                ? "Nothing pending"
                : `Generate Tally Sales XML (${counts.sales.pending})`}
            </Button>
            <p className="text-xs text-[var(--muted)]">
              Party ledger = Customer <em>Legal Name</em> from Master Data. Intra-state
              invoices split CGST + SGST; inter-state emit IGST. HSN pulled from the line item.
            </p>
          </CardContent>
        </Card>

        {/* --- Purchase card --------------------------------------------- */}
        <Card className="bg-[var(--bg-1)] border-[var(--border)]" data-testid="tally-purchase-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              Purchase · GRN Bills
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <KpiCard label="Pending Sync" value={counts.purchase.pending} tone="warn" />
              <KpiCard label="Synced" value={counts.purchase.synced} tone="ok" />
            </div>
            <Button
              onClick={() => downloadXml("purchase")}
              disabled={busy.purchase || counts.purchase.pending === 0}
              className="w-full"
              data-testid="btn-tally-purchase-xml"
            >
              {busy.purchase
                ? "Generating…"
                : counts.purchase.pending === 0
                ? "Nothing pending"
                : `Generate Tally Purchase XML (${counts.purchase.pending})`}
            </Button>
            <p className="text-xs text-[var(--muted)]">
              Party ledger = Supplier <em>Legal Name</em>. Multi-line GRNs collapse
              into a single Tally Purchase voucher via <code>transfer_id</code>.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* --- Recent Sync Runs table (Round-Trip verify) --- */}
      <Card className="bg-[var(--bg-1)] border-[var(--border)]" data-testid="tally-runs-card">
        <CardHeader>
          <CardTitle className="text-white text-lg">Recent Sync Runs</CardTitle>
        </CardHeader>
        <CardContent>
          {runs.length === 0 ? (
            <p className="text-sm text-[var(--muted)]">No sync runs yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-[var(--muted)]">
                <tr className="border-b border-[var(--border)]">
                  <th className="text-left py-2">When</th>
                  <th className="text-left">Kind</th>
                  <th className="text-right">Docs</th>
                  <th className="text-left">Actor</th>
                  <th className="text-left">Reconciled</th>
                  <th className="text-right">Verify</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} className="border-b border-[var(--border)]/40" data-testid={`tally-run-${r.id}`}>
                    <td className="py-2 text-[var(--muted)]">{(r.created_at || "").slice(0, 19).replace("T", " ")}</td>
                    <td className={r.kind === "sales" ? "text-blue-400" : "text-purple-400"}>{r.kind}</td>
                    <td className="text-right text-white tabular-nums">{r.count}</td>
                    <td className="text-[var(--muted)]">{r.actor || "—"}</td>
                    <td className={r.reconciled_at ? (r.reconcile_matched ? "text-emerald-400" : "text-amber-400") : "text-[var(--muted)]"}>
                      {r.reconciled_at
                        ? (r.reconcile_matched
                            ? `✓ ${(r.tally_voucher_ids || []).length}/${r.count}`
                            : `⚠ ${(r.tally_voucher_ids || []).length}/${r.count} mismatch`)
                        : "—"}
                    </td>
                    <td className="text-right">
                      <Button size="sm" variant="outline" onClick={() => { setReconcileRow(r); setVoucherText((r.tally_voucher_ids || []).join(", ")); }} data-testid={`btn-tally-verify-${r.id}`}>
                        {r.reconciled_at ? "Re-verify" : "Verify"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* --- Reconcile dialog --- */}
      <Dialog open={!!reconcileRow} onOpenChange={(o) => !o && setReconcileRow(null)}>
        <DialogContent
          className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-lg"
          description="Paste the voucher IDs Tally returned after import. Count matches confirm all vouchers made it in."
        >
          <DialogHeader>
            <DialogTitle>Round-Trip Verify · {reconcileRow?.kind}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-[var(--muted)]">
              MES exported <b className="text-white">{reconcileRow?.count}</b> voucher{reconcileRow?.count === 1 ? "" : "s"}. Paste comma / newline-separated Tally voucher IDs below.
            </div>
            <Textarea
              value={voucherText}
              onChange={(e) => setVoucherText(e.target.value)}
              placeholder="V-001, V-002, V-003 …"
              rows={6}
              className="bg-[var(--bg-2)]"
              data-testid="tally-verify-textarea"
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setReconcileRow(null)}>Cancel</Button>
              <Button
                onClick={async () => {
                  const ids = voucherText.split(/[,\n]+/).map((s) => s.trim()).filter(Boolean);
                  try {
                    const { data } = await api.post(`/tally/runs/${reconcileRow.id}/reconcile`, { tally_voucher_ids: ids });
                    if (data.matched) toast.success(`Match confirmed — ${data.mes_count}/${data.tally_count}`);
                    else toast.warning(`Mismatch — MES ${data.mes_count} vs Tally ${data.tally_count}`);
                    setReconcileRow(null);
                    await load();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Reconcile failed");
                  }
                }}
                data-testid="btn-tally-verify-submit"
              >
                Verify
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
